import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, finalize, fromEvent, Subject, Subscription, takeUntil, firstValueFrom } from 'rxjs';
import { INDIA_STATES_TAMIL } from '@/data/states';
import { INDIA_STATE_DISTRICTS } from '@/data/state-districts';
import Swal from 'sweetalert2';
import { ActivatedRoute } from '@angular/router';
import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode-generator';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss']
})
export class RegistrationComponent implements OnInit, AfterViewInit, OnDestroy {
    destroy$: Subject<boolean> = new Subject<boolean>();
    @ViewChild('pageHeader', { static: true })
    pageHeader!: ElementRef<HTMLElement>;

    @ViewChild('bgWrapper', { static: true })
    bgWrapper!: ElementRef<HTMLElement>;

    statesList = INDIA_STATES_TAMIL;
    districtList: any[] = [];
    currentYear = new Date().getFullYear();
    resizeSub?: Subscription;

    getEventCity: string = "";
    eventDetails: EventBookingDetails;
    canShow: boolean = false;

    remainingCount: number = 0;
    currentWhatsappNumber: any;
    ticketDetails: any;
    qrCodeImg: string = "";
    pageNotFound: boolean = false;
    allowedDomains = ['gmail.com', 'zohomail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];


    private skipUpdateRows = false;
    constructor(
        private fb: FormBuilder,
        private appService: AppService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private renderer: Renderer2,
        private route: ActivatedRoute
    ) { }

    // ---------------- FORM INITIALIZATION ----------------
    eventForm = this.fb.group({
        emailId: ['', [Validators.required, Validators.email, this.emailDomainValidator()]],
        name: ['', [
            Validators.required,
            Validators.pattern(/^[A-Za-z.]+$/)
        ]],
        phoneNumber: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]],
        whatsappNumber: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]],
        state: ['', Validators.required],
        address: ['', Validators.required],
        district: ['', Validators.required],
        age: ['', [
            Validators.required,
            Validators.min(1),
            Validators.max(120),
            Validators.pattern(/^(?:1[01][0-9]|120|[1-9]?[0-9])$/)
        ]],
        physically: ['', Validators.required],
        ticketCount: [0],
        participants: [0, Validators.required],
        noScam: [false, Validators.requiredTrue],
        willBring: [false],
        willAttend: [''],
        eventId: [0],
        participantInputs: this.fb.array([])
    });

    get members() {
        return this.eventForm.get('participantInputs') as FormArray;
    }

    createMemberGroup() {
        return this.fb.group({
            participantName: ['', [Validators.required, Validators.minLength(3)]],
            whatsappNumber: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]]
        }, { validators: this.validateNotSameAsMain() });
    }


    emailDomainValidator() {
        return (control: AbstractControl) => {
            const value = control.value || '';
            const domain = value.split('@')[1]?.toLowerCase();
            if (!domain) return null;

            return this.allowedDomains.includes(domain)
                ? null
                : { invalidDomain: true };
        };
    }

    validateNotSameAsMain() {
        return (group: AbstractControl) => {
            if (!this.eventForm) return null;

            const mainName = this.eventForm.get('name')?.value?.trim()?.toLowerCase();
            const mainPhone = this.eventForm.get('whatsappNumber')?.value;

            const pName = group.get('participantName')?.value?.trim()?.toLowerCase();
            const pPhone = group.get('whatsappNumber')?.value;

            let hasError = false;

            // Validate Name
            if (pName && mainName && pName === mainName) {
                group.get('participantName')?.setErrors({ sameAsMain: true });
                hasError = true;
            } else {
                // remove error only if exists
                if (group.get('participantName')?.hasError('sameAsMain')) {
                    let errors = group.get('participantName')?.errors;
                    delete errors?.['sameAsMain'];
                    if (Object.keys(errors).length === 0) group.get('participantName')?.setErrors(null);
                }
            }

            // Validate Phone
            if (pPhone && mainPhone && pPhone === mainPhone) {
                group.get('whatsappNumber')?.setErrors({ sameAsMainPhone: true });
                hasError = true;
            } else {
                if (group.get('whatsappNumber')?.hasError('sameAsMainPhone')) {
                    let errors = group.get('whatsappNumber')?.errors;
                    delete errors?.['sameAsMainPhone'];
                    if (Object.keys(errors).length === 0) group.get('whatsappNumber')?.setErrors(null);
                }
            }

            return hasError ? { duplicateMain: true } : null;
        };
    }


    // ------------------- ngOnInit -------------------
    ngOnInit() {
        // Load encrypted event ID
        this.route.queryParams.subscribe((params) => {
            const cityValues = params['city'] || params[""];
            if (cityValues) {
                this.getEventCity = cityValues;
                this.getEventDetailsCity();
            }
        });

        // Disable all main fields at start (except participants)
        Object.keys(this.eventForm.controls).forEach((key) => {
            if (key !== 'noScam' && key !== 'participants') {
                this.eventForm.get(key)?.disable({ emitEvent: false });
            }
        });

        // 3️⃣ Enable all fields after noScam is checked
        this.eventForm.get('noScam')?.valueChanges.subscribe((checked: boolean) => {
            if (checked) {

                // Enable all controls
                Object.keys(this.eventForm.controls).forEach((key) => {
                    this.eventForm.get(key)?.enable({ emitEvent: false });
                });

                // IMPORTANT: Always enable FormArray
                this.members.enable({ emitEvent: false });

            } else {

                // Disable main fields except: noScam, participants, participantInputs
                Object.keys(this.eventForm.controls).forEach((key) => {
                    if (key !== 'noScam' && key !== 'participants' && key !== 'participantInputs') {
                        this.eventForm.get(key)?.disable({ emitEvent: false });
                    }
                });

                // IMPORTANT: Keep FormArray enabled
                this.members.enable({ emitEvent: false });
            }
        });

        // 4️⃣ Auto copy phone → whatsapp
        this.eventForm.get('phoneNumber')?.valueChanges.subscribe((value) => {
            const whatsappControl = this.eventForm.get('whatsappNumber');

            // Copy phone → whatsapp ONLY if user has not typed manually
            if (!whatsappControl?.dirty) {
                whatsappControl?.setValue(value, { emitEvent: false });
            }
        });

        // 5️⃣ Update participant rows
        this.eventForm.get('participants')?.valueChanges.subscribe((count) => {
            if (this.skipUpdateRows) return;   // 🚀 NEW: cancel update during submit

            this.updateMemberRows(+count);
        });

        // 6️⃣ State → District
        this.eventForm.get('state')?.valueChanges.subscribe((stateValue) => {
            this.loadDistricts(stateValue);
        });
    }

    // ---------------- HEADER MARGIN ADJUST ----------------
    ngAfterViewInit() {
        this.adjustContentMargin();
        this.resizeSub = fromEvent(window, 'resize')
            .pipe(debounceTime(120))
            .subscribe(() => this.adjustContentMargin());
    }

    adjustContentMargin() {
        const headerEl = this.pageHeader?.nativeElement;
        const wrapperEl = this.bgWrapper?.nativeElement;
        if (headerEl && wrapperEl) {
            const height = headerEl.offsetHeight;
            this.renderer.setStyle(wrapperEl, 'margin-top', `${height}px`);
        }
    }

    // ------------------- HELPERS -------------------
    loadDistricts(stateValue: string) {
        this.districtList = INDIA_STATE_DISTRICTS[stateValue] || [];
        this.eventForm.get('district')?.setValue('');
    }

    allowOnlyNumbers(event: any) {
        event.target.value = event.target.value.replace(/[^0-9]/g, '');
    }

    allowOnlyAge(event: KeyboardEvent) {
        const input = event.target as HTMLInputElement;
        const value = input.value;      // current input value
        const key = event.key;          // key pressed

        // allow control keys: backspace, delete, arrows, tab
        if (key.length > 1) return;

        // only digits allowed
        if (!/^[0-9]$/.test(key)) {
            event.preventDefault();
            return;
        }

        // first character cannot be 0
        if (value.length === 0 && key === '0') {
            event.preventDefault();
            return;
        }

        // build the new value if key is allowed
        const newValue = value + key;
        const num = parseInt(newValue, 10);

        // prevent numbers > 120
        if (num > 120) {
            event.preventDefault();
            return;
        }
    }



    allowOnlyName(event: KeyboardEvent) {
        const char = event.key;

        // Allow only A-Z, a-z, dot
        const pattern = /^[A-Za-z.]$/;

        if (!pattern.test(char)) {
            event.preventDefault();
        }
    }

    allowOnlyDigits(event: KeyboardEvent) {
        if (!/^[0-9]$/.test(event.key) && event.key.length === 1) {
            event.preventDefault();
        }
    }


    // Common phone handler for BOTH phoneNumber & whatsappNumber
    onPhoneInput(event: Event, controlName: string) {
        const input = event.target as HTMLInputElement;
        let val = input.value.replace(/\D/g, '');

        // must start with 6-9
        if (val.length > 0 && !/^[6-9]/.test(val)) {
            val = val.substring(1);
        }

        // limit 10 digits
        val = val.substring(0, 10);

        input.value = val;
        this.eventForm.get(controlName)?.setValue(val, { emitEvent: false });
    }

    // Paste handler
    onPhonePaste(event: ClipboardEvent, controlName: string) {
        event.preventDefault();
        let val = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").substring(0, 10);

        if (val.length > 0 && !/^[6-9]/.test(val)) {
            val = val.replace(/^[0-5]+/, "");
        }

        const input = event.target as HTMLInputElement;
        input.value = val;

        this.eventForm.get(controlName)?.setValue(val, { emitEvent: false });
    }


    duplicatePhoneandEmailCheck(phone: string, email: string) {
        const fullPhone = phone ? "91" + phone : "";

        const formData = {
            eventId: this.eventDetails?.eventId,
            phoneNumber: fullPhone,
            email: email || ""
        };

        this.appService.duplicatePhoneandEmailCheck(formData).subscribe({
            next: (res) => {
                // No duplicate → clear errors
                this.eventForm.get('whatsappNumber')?.setErrors(null);
                this.eventForm.get('emailId')?.setErrors(null);
            },
            error: (err) => {
                if (err.status === 409) {
                    if (err.error.message.includes('Email')) {
                        this.eventForm.get('emailId')?.setErrors({ duplicateEmail: true });
                    }
                    if (err.error.message.includes('Whatsapp') || err.error.message.includes('Mobile')) {
                        this.eventForm.get('whatsappNumber')?.setErrors({ duplicatePhone: true });
                    }
                }
            }
        });
    }




    updateMemberRows(count: number) {
        const existing = this.members.length;

        if (existing === count) return;

        if (existing > count) {
            for (let i = existing - 1; i >= count; i--) {
                this.members.removeAt(i);
            }
            return;
        }

        for (let i = existing; i < count; i++) {
            this.members.push(this.createMemberGroup());
        }
    }

    // -------------------- SUBMIT --------------------
    submitForm() {

        this.eventForm.markAllAsTouched();

        // 1️⃣ STOP if ANY invalid fields
        if (this.eventForm.invalid) {
            console.warn("Form invalid → stopping submit");
            return;
        }

        // 2️⃣ EMAIL DOMAIN CHECK
        const email = this.eventForm.get('emailId')?.value;
        const domain = email?.split('@')[1]?.toLowerCase();

        if (!this.allowedDomains.includes(domain)) {
            this.eventForm.get('emailId')?.setErrors({ invalidDomain: true });
            return;
        }

        // 4️⃣ SET EVENT ID
        this.eventForm.get('eventId')?.setValue(Number(this.eventDetails?.eventId));

        // 5️⃣ CALCULATE ticketCount
        const participants = Number(this.eventForm.get('participants')?.value || 0);
        this.eventForm.get('ticketCount')?.setValue(participants + 1);

        // 6️⃣ MERGE participant list
        const mainParticipant = {
            participantName: this.eventForm.get('name')?.value,
            whatsappNumber: this.eventForm.get('phoneNumber')?.value
        };

        if (this.eventForm.get('emailId')?.errors?.['duplicateEmail']) {
            this.toastr.error("இந்த மின்னஞ்சல் ஏற்கனவே பதிவுசெய்யப்பட்டுள்ளது.");
            return;
        }

        // STOP if duplicate phone
        if (this.eventForm.get('whatsappNumber')?.errors?.['duplicatePhone']) {
            this.toastr.error("இந்த தொலைபேசி எண் ஏற்கனவே பயன்படுத்தப்பட்டுள்ளது.");
            return;
        }

        const additionalParticipants = this.members.getRawValue();

        this.skipUpdateRows = true;
        this.eventForm.patchValue({
            participantInputs: additionalParticipants
        });
        this.skipUpdateRows = false;


        const finalData = this.eventForm.getRawValue();
        console.log("FINAL SUBMIT DATA:", finalData);

        // CALL API ONLY AFTER ALL VALIDATION PASSES
        this.saveRegisteredDetails(finalData);
    }



    async saveRegisteredDetails(data: any) {

        this.currentWhatsappNumber = data.whatsappNumber;

        // Prefix 91 if missing
        if (this.currentWhatsappNumber && !this.currentWhatsappNumber.startsWith('91')) {
            this.eventForm.get('whatsappNumber')
                ?.setValue('91' + this.currentWhatsappNumber, { emitEvent: false });
        }

        this.spinner.show();

        // Disable browser back button
        history.pushState(null, '', location.href);
        const blockBack = () => history.pushState(null, '', location.href);
        window.addEventListener('popstate', blockBack);

        try {

            // -------------------------------------------------
            // SAVE REGISTRATION (API)
            // -------------------------------------------------
            const res: any = await this.appService
                .saveEventRegisteredDetails(data)
                .toPromise();

            this.ticketDetails = res;

            if (!this.ticketDetails?.audienceDetails?.length) {
                throw new Error('Ticket data missing');
            }

            const ticketSeq =
                this.ticketDetails.audienceDetails[0].groupTicketSeq;

            // -------------------------------------------------
            // WAIT FOR DOM RENDER
            // -------------------------------------------------
            // await new Promise(resolve => setTimeout(resolve, 300));

            // const element = document.getElementById('ticket-wrapper');
            // if (!element) {
            //     throw new Error('Ticket DOM not rendered');
            // }

            // // -------------------------------------------------
            // // GENERATE QR
            // // -------------------------------------------------
            // this.qrCodeImg = await this.generateQRCode(ticketSeq);

            // await new Promise(resolve => setTimeout(resolve, 200));

            // // -------------------------------------------------
            // // CONVERT TICKET TO IMAGE
            // // -------------------------------------------------
            // const canvas = await html2canvas(element, {
            //     scale: 2,
            //     useCORS: true,
            //     backgroundColor: null
            // });

            // const url = canvas.toDataURL('image/png');

            // // Auto download
            // const a = document.createElement('a');
            // a.href = url;
            // a.download = `Ticket-${ticketSeq}.png`;
            // document.body.appendChild(a);
            // a.click();
            // document.body.removeChild(a);

            this.spinner.hide();

            // -------------------------------------------------
            // SUCCESS ALERT
            // -------------------------------------------------
            await Swal.fire({
                icon: 'success',
                title: '',
                html: `
        <div style="font-size:15px; line-height:1.9; text-align:center;">

            <div style="font-size:18px; font-weight:600; margin-bottom:15px;">
                டிக்கெட் பதிவு வெற்றிகரமாக முடிந்தது
            </div>

            <p style="font-size:28px; margin:0;">
                🎫 உங்கள் டிக்கெட் எண் :
            </p>

            <b style="font-size:32px; color:#d63384;">
                ${ticketSeq}
            </b>

            <br><br>

            📌 இந்த டிக்கெட் எண்ணை ஸ்கிரீன்‌ஷாட் எடுத்து அல்லது குறிப்பாக பதிவு செய்து வைத்துக்கொள்ளுங்கள்.

            <br><br>

            👥 வாட்ஸ்அப் குழுவில் சேர <b>OK</b> பொத்தானை அழுத்தவும்.

        </div>
    `,
                confirmButtonText: 'OK',
                confirmButtonColor: '#28a745',
                allowOutsideClick: false,
                allowEscapeKey: false
            });


            // -------------------------------------------------
            // WHATSAPP GROUP POPUP
            // -------------------------------------------------
            const groupLink = this.eventDetails?.whatsappGroupLink;

            if (groupLink) {
                const groupQrUrl =
                    'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' +
                    encodeURIComponent(groupLink);

                await Swal.fire({
                    icon: 'info',
                    title: 'வாட்ஸ் ஆப் குழு இணைப்பு',
                    html: `
                  <p style="font-size:15px;text-align:center">
                    <b>இந்த WhatsApp குழுவில் சேர:</b><br><br>
                    🔹 Link கிளிக் செய்யவும் அல்லது<br>
                    🔹 QR Code scan செய்யவும்
                  </p>
                  <div style="text-align:center">
                    <img src="${groupQrUrl}" width="180" />
                  </div>
                `,
                    confirmButtonText: 'WhatsApp குழுவில் சேர',
                    confirmButtonColor: '#25D366',
                    allowOutsideClick: false,
                    preConfirm: () => window.open(groupLink, '_blank')
                });
            }

            // -------------------------------------------------
            // FINAL RELOAD
            // -------------------------------------------------
            window.location.reload();

        } catch (err: any) {

            this.spinner.hide();

            // Restore original number
            this.eventForm.controls['whatsappNumber']
                ?.setValue(this.currentWhatsappNumber);

            this.toastr.error(
                err?.error?.message ||
                err?.message ||
                'பதிவு செய்ய முடியவில்லை. மீண்டும் முயற்சிக்கவும்'
            );

            console.error(err);

        } finally {

            // Re-enable back button
            window.removeEventListener('popstate', blockBack);
        }
    }




    get maxSelectable(): number {
        return this.remainingCount - 1; // Eg: remainingCount=3 → maxSelectable=2
    }

    getEventDetailsCity() {
        this.canShow = false;
        this.pageNotFound = false;
        this.spinner.show();
        this.appService.getCityBasedEventDetails(this.getEventCity)
            .pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: (data) => {
                    this.eventDetails = data;

                    console.log(this.eventDetails)

                    const totalTicket = this.eventDetails?.bookingCount;
                    const bookedTicket = this.eventDetails?.bookedCount;
                    this.remainingCount = totalTicket - bookedTicket;
                    // this.remainingCount = totalTicket - bookedTicket;
                    console.log(this.remainingCount)
                    this.checkEventConditions();  // your logic
                }, error: (err) => {
                    const msg = err.error?.message || err.message;
                    this.toastr.error(msg);

                    // Set flags based on error message
                    if (msg === 'Event is already completed') {
                        this.canShow = true;
                    }
                    if (msg === 'Data not found') {
                        this.pageNotFound = true;
                    }
                }
            });
    }


    checkEventConditions() {
        if (!this.eventDetails) {
            this.canShow = false;
            return;
        }

        const eventEnd = Number(this.eventDetails.endDate);  // ensure epoch ms number
        const bookingCount = this.eventDetails.bookingCount ?? 0;
        const bookedCount = this.eventDetails.bookedCount ?? 0;

        // Get today's start timestamp (00:00:00)
        const now = new Date();
        const todayStart = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0, 0, 0, 0
        ).getTime();

        // Event expired only if endDate is before today's beginning
        const eventExpired = eventEnd < todayStart;

        // If all tickets sold
        const ticketsCompleted = bookedCount >= bookingCount;

        this.canShow = eventExpired || ticketsCompleted;
    }


    onHeaderImageLoad(): void {
        // Small timeout ensures browser layout has stabilized (optional)
        setTimeout(() => {
            this.adjustContentMargin();
        }, 20);
    }

    ngOnDestroy() {
        this.resizeSub?.unsubscribe();
    }

    async generateQRCode(ticketNo: string): Promise<string> {
        const qr = QRCode(0, 'M');
        qr.addData(ticketNo);
        qr.make();

        const cellSize = 4;
        const margin = 0;

        const dataURL = qr.createDataURL(cellSize, margin);

        return await this.convertWhiteToTransparent(dataURL);
    }
    private convertWhiteToTransparent(dataUrl: string): Promise<string> {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = dataUrl;

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d")!;

                canvas.width = img.width;
                canvas.height = img.height;

                ctx.drawImage(img, 0, 0);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;

                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];

                    // Black pixels → WHITE
                    if (r < 50 && g < 50 && b < 50) {
                        data[i] = 255;     // R
                        data[i + 1] = 255; // G
                        data[i + 2] = 255; // B
                        data[i + 3] = 255; // Opaque
                    }

                    // White pixels → Transparent
                    if (r > 240 && g > 240 && b > 240) {
                        data[i + 3] = 0; // transparent
                    }
                }

                ctx.putImageData(imageData, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            };
        });
    }

}
